import React, { useState, useEffect, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@mui/material";
import { AiOutlineSearch } from "react-icons/ai";
import { MdPersonAddDisabled } from "react-icons/md";
import { BsFillPersonCheckFill } from "react-icons/bs";
import {
  obtenerEstudiantes,
  inhabilitarEstudiante,
  habilitarEstudiante,
  verificarSolvenciaEstudiante,
  obtenerEstudiantePorId,
} from "../../../services/EstudianteService";
import Swal from "sweetalert2";
import JumpByItemFooter from "../customTableFooter/JumpByItemFooter";
import "./estudiantesHistorial.css";

function EstudiantesHistorial() {
  const [estudiantes, setEstudiantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cifBuscado, setCifBuscado] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [pageId, setPageId] = useState(0);
  const [pagingMetadata, setPagingMetadata] = useState({});
  const [focusedUser, setFocusedUser] = useState(null);

  const fetchEstudiantes = async () => {
    setLoading(true);
    try {
      const { content, ...metadata } = await obtenerEstudiantes(
        pageId,
        pageSize,
      );
      setEstudiantes(content);
      setPagingMetadata(metadata);
    } catch (error) {
      console.error("Error al obtener estudiantes", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEstudiantes();
  }, [pageId, pageSize]);

  const handleCambiarEstadoEstudiante = async (cif, habilitar) => {
    try {
      const action = habilitar ? habilitarEstudiante : inhabilitarEstudiante;
      await action(cif);
      Swal.fire({
        title: "Éxito!",
        text: `El estudiante ha sido ${habilitar ? "habilitado" : "inhabilitado"} con éxito.`,
        icon: "success",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
      fetchEstudiantes();
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: `No se pudo ${habilitar ? "habilitar" : "inhabilitar"} al estudiante.`,
        icon: "error",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
    }
  };

const handleVerificarSolvencia = async () => {
  const cif = cifBuscado.trim();
  if (!cif) {
    Swal.fire({
      title: "Campo vacío",
      text: "Ingresa un CIF para verificar.",
      icon: "info",
      confirmButtonText: "Ok",
      confirmButtonColor: "#21807C",
    });
    return;
  }

  // 1) Abrir Swal con loading inmediatamente
  Swal.fire({
    title: "Verificando solvencia…",
    html: '<p style="margin:6px 0 0">Consultando usuario y estado</p>',
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });

  try {
    // 2) Hacer ambas consultas en paralelo
    const [solvencia, userResp] = await Promise.all([
      verificarSolvenciaEstudiante(cif),
      obtenerEstudiantePorId(cif),
    ]);

    // 3) Enfocar usuario en la tabla (si existe)
    const user = userResp?.data ?? userResp;
    if (user && user.cif) {
      setFocusedUser(user);
    } else {
      setFocusedUser(null);
    }

    // 4) Actualizar el mismo Swal con el resultado
    Swal.update({
      title: "Solvencia del Estudiante",
      html: solvencia.mensaje,
      icon: solvencia.solvente ? "success" : "warning",
      confirmButtonText: "Ok",
      confirmButtonColor: "#21807C",
      allowOutsideClick: true,
      allowEscapeKey: true,
    });
    Swal.hideLoading();
  } catch (error) {
    setFocusedUser(null);
    Swal.update({
      title: "Error",
      html: "No se pudo verificar la solvencia.",
      icon: "error",
      confirmButtonText: "Ok",
      confirmButtonColor: "#21807C",
      allowOutsideClick: true,
      allowEscapeKey: true,
    });
    Swal.hideLoading();
  }
};


  const rowsToRender = useMemo(() => {
    if (!focusedUser) return estudiantes;

    const idx = estudiantes.findIndex((e) => e.cif === focusedUser.cif);
    if (idx !== -1) {
      const rest = [...estudiantes];
      rest.splice(idx, 1);
      return [focusedUser, ...rest];
    }
    // no está en esta página: lo prependemos y recortamos para mantener tamaño
    return [focusedUser, ...estudiantes.slice(0, Math.max(0, pageSize - 1))];
  }, [focusedUser, estudiantes, pageSize]);

  const clearFocus = () => setFocusedUser(null);

  const columnas = [
    {
      field: "cif",
      headerName: "CIF",
      width: 180,
      headerClassName: "tablaInventario-headers",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "userPic",
      headerName: "Foto de Perfil",
      sortable: false,
      width: 150,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      renderCell: (params) => (
        <img
          src={params.value}
          alt="Autor Foto de Perfil"
          style={{
            width: "71px",
            height: "71px",
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
      ),
    },
    {
      field: "nombreCompleto",
      headerName: "Nombre",
      width: 250,
      headerClassName: "tablaInventario-headers",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "correoInstitucional",
      headerName: "Correo Institucional",
      width: 250,
      headerClassName: "tablaInventario-headers",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "telefonoCelular",
      headerName: "Teléfono",
      width: 190,
      headerClassName: "tablaInventario-headers",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 150,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      renderCell: (params) => {
        return (
          <span>{params.row.estado ? "Habilitado" : "Deshabilitado"}</span>
        );
      },
    },
    {
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      width: 150,
      headerAlign: "center",
      align: "center",
      headerClassName: "tablaInventario-headers",
      renderCell: (params) => (
        <Button
          className={
            params.row.estado
              ? "tableInventario-deleteBtn"
              : "tableEstudiantes-habilitarBtn"
          }
          style={{
            width: "40px",
            height: "40px",
          }}
          onClick={() =>
            handleCambiarEstadoEstudiante(params.row.cif, !params.row.estado)
          }
        >
          {params.row.estado ? (
            <MdPersonAddDisabled color="white" size={22} />
          ) : (
            <BsFillPersonCheckFill color="white" size={22} />
          )}
        </Button>
      ),
    },
  ];


    return (
      <div className="estudiantesHistorial-container">
        <h1>Usuarios</h1>
        <div className="estudianteHistorial-Top">
        <div className="inventario-searchBar">
          <AiOutlineSearch size={20} style={{ color: "#909090" }} />
          <input
            type="text"
            placeholder="Cif Usuario"
            value={cifBuscado}
            onChange={(e) => setCifBuscado(e.target.value)}
          />
        </div>

        <button onClick={handleVerificarSolvencia}>Ver Solvencia</button>
        {focusedUser && (
          <button
            onClick={clearFocus}
            style={{
              width: "120px",
              height: "2.5rem",
              cursor: "pointer",
              background: "transparent",
              border: "1px solid #21807C",
              padding: "8px 12px",
              borderRadius: 8,
              color: "#21807C",
              fontWeight: "bold",
            }}
          >
            Limpiar
          </button>
        )
        }
      </div>

        <div className="tablaEstudiantesContainer">
          <DataGrid
            loading={loading}
            rows={rowsToRender}
            columns={columnas}
            rowCount={pagingMetadata.totalElements}
            paginationMode="server"
            paginationModel={{
              pageSize: pageSize,
              page: pageId,
            }}
            getRowId={(row) => row.cif}
            rowHeight={80}
            onPaginationModelChange={(model) => {
              setPageId(model.page);
              setPageSize(model.pageSize);
            }}
            getRowClassName={(params) =>
              focusedUser && params.id === focusedUser.cif ? "row-focused" : ""
            }
            slots={{ footer: JumpByItemFooter }}
            slotProps={{
              footer: {
              total: pagingMetadata.totalElements ?? 0,
              page: pageId,
              pageSize,
              onJumpPage: (p) => setPageId(p),
              noun: "usuario",  
              },
            }}
          />
        </div>
      </div>
    );
}

export default EstudiantesHistorial;

